'use client';

/**
 * SplitPanelFooter Component
 *
 * Footer section for SplitPanel with action buttons.
 */

import { alpha, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';

// ============================================================================
// Types
// ============================================================================

export interface SplitPanelFooterProps {
  /** Footer content (typically action buttons) */
  children: React.ReactNode;
  /** Alignment of footer content */
  align?: 'left' | 'center' | 'right' | 'space-between';
}

// ============================================================================
// Styled Components
// ============================================================================

interface FooterContainerProps {
  $align: 'left' | 'center' | 'right' | 'space-between';
}

const FooterContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<FooterContainerProps>(({ theme, $align }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent:
    $align === 'left'
      ? 'flex-start'
      : $align === 'right'
        ? 'flex-end'
        : $align === 'center'
          ? 'center'
          : 'space-between',
  gap: theme.spacing(1),
  padding: theme.spacing(1.25, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.02) : alpha(theme.palette.common.black, 0.02),
  flexShrink: 0,
}));

// ============================================================================
// Component
// ============================================================================

export const SplitPanelFooter: React.FC<SplitPanelFooterProps> = ({ children, align = 'right' }) => {
  return <FooterContainer $align={align}>{children}</FooterContainer>;
};

export default SplitPanelFooter;
